<template>
  <div class="py-3">
    <div class="p-toolbar">
      <div class="p-toolbar-item p-search">
        <p-search-input
          v-model="search"
          :items="searchSuggestions"
          @submit="searchContractTemplates"
        ></p-search-input>
      </div>
      <div v-if="hasAccess()" class="p-toolbar-item p-right">
        <v-btn
          class="p-btn-rounded mr-4"
          large
          rounded
          @click.stop="openEditDialog(null)"
        >
          {{ $t('contractTemplate.createTemplate') }}
        </v-btn>
      </div>
    </div>
    <v-row v-if="contractTemplates.length > 0">
      <v-col>
        <v-data-table
          :headers="tableHeaders"
          :items="contractTemplatesComputed"
          :options.sync="tableOptions"
          :server-items-length="contractTemplatesTotalCount"
          class="p-no-borders"
          disable-filtering
          hide-default-footer
          @page-count="pageCount = $event"
          @update:options="reloadContractTemplates"
        >
          <template v-slot:[`item.changed`]="{ item }">
            <v-tooltip v-if="item.changed" bottom>
              <template v-slot:activator="{ on }">
                <v-btn icon v-on="on">
                  <span v-if="item.changed" class="item-changed"></span>
                </v-btn>
              </template>
              {{ $t('contractTemplate.table.changedTooltip') }}
            </v-tooltip>
          </template>
          <template v-slot:[`item.promoter`]="{ item }">
            <v-icon v-if="item.promoter" color="primary">mdi-check</v-icon>
          </template>
          <template v-slot:[`item.contractReceivers`]="{ item }">
            <div class="d-flex">
              <p-contact-item
                v-for="contractReceiverId in item.contractReceivers"
                :key="contractReceiverId"
                :contact="shortContactById(contractReceiverId)"
                avatar-class="mr-2"
                font-bold
                avatarOnly
              ></p-contact-item>
            </div>
          </template>
          <template v-if="hasAccess()" v-slot:[`item.actions`]="{ item }">
            <div class="p-table-row-actions">
              <p-btn-icon type="edit" @click="openEditDialog(item.id)"></p-btn-icon>
              <p-btn-icon type="delete" @click="deleteTemplate(item)"></p-btn-icon>
            </div>
          </template>
        </v-data-table>

        <v-pagination
          v-model="tablePage"
          :length="pageCount"
          :total-visible="15"
          class="mt-5"
        ></v-pagination>
      </v-col>
    </v-row>

    <v-row v-else-if="contractTemplatesLoaded && !search">
      <v-col>
        <v-divider class="mb-16"></v-divider>
        <p-no-data
          img="contract.png"
          :text="$t('contractTemplate.noTemplates')"
          :btn-text="$t('contractTemplate.createTemplate')"
          :hide-btn="!hasAccess()"
          @btn-click="openEditDialog(null)"
        ></p-no-data>
      </v-col>
    </v-row>

    <template-edit-dialog
      :dialog.sync="editDialog"
      :template="editedTemplate"
    ></template-edit-dialog>
  </div>
</template>


<script>
import { mapGetters, mapActions, mapState } from 'vuex'

import PSearchInput from '@/components/PSearchInput'
import PBtnIcon from '@/components/PBtnIcon'
import PContactItem from '@/components/PContactItem'
import PNoData from '@/components/PNoData'

import TemplateEditDialog from './ContractTemplateEditDialog'

import snackbarMixin from '@/mixins/snackbar.mixin'
import pageMixin from '@/mixins/page.mixin'
import accessMixin from '@/mixins/access.mixin'
import userSettingsMixin from '@/mixins/userSettings.mixin'


import eventBus from '@/eventBus'

export default {
  components: {
    PSearchInput,
    PBtnIcon,
    TemplateEditDialog,
    PContactItem,
    PNoData
  },


  mixins: [
    snackbarMixin,
    pageMixin,
    accessMixin,
    userSettingsMixin
  ],


  data () {
    return {
      search: '',
      editDialog: false,
      editedTemplate: null,
      tableOptions: {
        page: 1,
        itemsPerPage: 20,
        sortBy: [],
        sortDesc: []
      },
      contractTemplatesLoaded: false,
      pageCount: 0,
      tablePage: 1
    }
  },

  watch: {
    currentSeasonId () {
      this.reloadContractTemplates(1)
    },

    tablePage (newTablePage) {
      this.tableOptions.page = newTablePage
      this.reloadContractTemplates(newTablePage)
    }
  },

  computed: {
    ...mapState('contractTemplate', ['contractTemplatesTotalCount']),
    ...mapGetters('contractTemplate', ['contractTemplates', 'contractTemplateById']),
    ...mapGetters('contact', ['shortContacts', 'shortContactById']),
    ...mapGetters('changes', ['isItemChanged']),

    contractTemplatesComputed () {
      return this.contractTemplates.map(contractTemplate => {
        return {
          ...contractTemplate,
          changed: this.isItemChanged(contractTemplate.id)
        }
      })
    },

    tableHeaders () {
      return [
        { text: this.$t('contractTemplate.name'), value: 'name' },
        { text: '', value: 'changed' },
        { text: this.$t('contractTemplate.promoter'), value: 'promoter' },
        { text: this.$t('contractTemplate.contractReceivers'), value: 'contractReceivers' },
        { text: '', value: 'actions', align: 'end', sortable: false, filterable: false }
      ]
    },

    searchSuggestions () {
      const items = this.contractTemplates.map(x => x.name)
      return items
    }
  },


  methods: {
    ...mapActions('contractTemplate', ['loadContractTemplates', 'loadContractTemplateById', 'deleteContractTemplate']),
    ...mapActions('contact', ['loadShortContacts']),
    ...mapActions('placeholder', ['loadPlaceholdersContractGlobal', 'loadPlaceholdersContractContact', 'loadPlaceholdersContractVenue']),


    initEventBus () {
      eventBus.$on('goToEntity', async (entityTitle) => {
        this.searchContractTemplates(entityTitle)
      })

      eventBus.$on('loadEntities', async () => {
        await this.loadContractTemplates()
      })
    },

    ...mapActions('user', ['loadUsersShort']),

    tableRowClassesReceivers (item) {
      if (!item.email) {
        return 'p-disabled'
      }
    },

    async openEditDialog (id) {
      try {
        let template = null

        if (id) {
          template = this.contractTemplateById(id)
          if (!template || !template.template) {
            this.startPageLoading()
            template = await this.loadContractTemplateById(id)
          }
        }

        this.editedTemplate = template
        this.editDialog = true
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      } finally {
        this.stopPageLoading()
      }
    },

    async deleteTemplate (template) {
      try {
        const confirm = await this.$confirm(this.$t('contractTemplate.deleteTemplateConfirm', template), { trueText: this.$t('delete') })
        if (confirm === true) {
          this.showSnackbarProcessing()
          await this.deleteContractTemplate(template.id)
          this.showSnackbarSuccess(this.$t('contractTemplate.templateDeleted'))
        }
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      }
    },

    async searchContractTemplates (val) {
      this.search = val
      this.tableOptions.page = 1
      await this.reloadContractTemplates()
    },

    async reloadContractTemplates () {
      this.saveTableOptions()
      try {
        this.startPageLoading()
        const sortBy = this.tableOptions.sortBy.length ? this.tableOptions.sortBy[0] : null
        const sortDesc = this.tableOptions.sortDesc.length ? this.tableOptions.sortDesc[0] : false

        await this.loadContractTemplates({
          skip: (this.tableOptions.page - 1) * this.tableOptions.itemsPerPage,
          take: this.tableOptions.itemsPerPage,
          search: this.search,
          sortBy,
          sortDesc
        })
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      } finally {
        this.stopPageLoading()
      }
    },

    saveTableOptions () {
      if (this.tableOptions.sortBy && this.tableOptions.sortBy.length) {
        this.userSettings.contractTemplatesTableOptionsSortBy = this.tableOptions.sortBy[0]
      }
      if (this.tableOptions.sortDesc && this.tableOptions.sortDesc.length) {
        this.userSettings.contractTemplatesTableOptionsSortDesc = this.tableOptions.sortDesc[0]
      }
      this.setUserSettings()
    },

    restoreTableOptions () {
      this.tableOptions.sortBy = []
      if (this.userSettings?.contractTemplatesTableOptionsSortBy) {
        this.tableOptions.sortBy.push(this.userSettings?.contractTemplatesTableOptionsSortBy)
      }

      this.tableOptions.sortDesc = []
      if (this.userSettings?.contractTemplatesTableOptionsSortDesc) {
        this.tableOptions.sortDesc.push(this.userSettings?.contractTemplatesTableOptionsSortDesc)
      }
    }
  },

  async created () {
    try {
      this.initEventBus()
      this.startPageLoading()
      await Promise.all([
        this.loadShortContacts(),
        this.loadPlaceholdersContractGlobal(),
        this.loadPlaceholdersContractContact(),
        this.loadPlaceholdersContractVenue(),
        this.loadUsersShort()
      ])

      await this.loadContractTemplates({ take: this.tableOptions.itemsPerPage })
      this.restoreTableOptions()
      this.contractTemplatesLoaded = true
      if (this.$router.currentRoute.params?.goto) {
        await this.searchContractTemplates(this.$router.currentRoute.params?.goto)
      }
    } catch (error) {
      this.showSnackbarError()
      this.$log.error(error)
    } finally {
      this.stopPageLoading()
    }
  }
}
</script>
